import { enqueueSnackbar } from 'notistack';
import { useState, useEffect, useCallback } from 'react';

import {
  Box,
  Button,
  Card,
  CircularProgress,
  IconButton,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';

import { useBoolean } from 'src/hooks/use-boolean';

import { fDate } from 'src/utils/format-time';
import { fCurrencyRupees } from 'src/utils/format-number';

import { useAppDispatcher } from 'src/redux/store';
import {
  deleteChequeSettlement,
  getChequeSettlements,
  getChequeSettlementSummary,
} from 'src/redux/slices/cheque';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';

import {
  ChequeSettlementSource,
  IChequeItem,
  IChequeSettlement,
  IChequeSettlementSummary,
} from 'src/types/cheque';

import SettlementDialog from './settlement-dialog';

type Props = {
  cheque: IChequeItem;
  // Bump to force a reload (e.g. after a recovery payment was added, which
  // can create a linked direct-to-supplier settlement).
  refreshKey: number;
  onChanged: VoidFunction;
};

function Row({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <Stack direction="row" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        {label}:
      </Typography>
      <Typography variant="body2" fontWeight="bold" color={color}>
        {value}
      </Typography>
    </Stack>
  );
}

export default function SettlementTab({ cheque, refreshKey, onChanged }: Props) {
  const dispatch = useAppDispatcher();

  const [settlements, setSettlements] = useState<IChequeSettlement[]>([]);
  const [summary, setSummary] = useState<IChequeSettlementSummary | null>(null);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState<IChequeSettlement | undefined>();
  const [deleting, setDeleting] = useState<IChequeSettlement | undefined>();

  const showDialog = useBoolean();
  const deleteLoading = useBoolean();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [list, sum] = await Promise.all([
        dispatch(getChequeSettlements({ chequeId: cheque._id })).unwrap(),
        dispatch(getChequeSettlementSummary({ chequeId: cheque._id })).unwrap(),
      ]);
      setSettlements(list);
      setSummary(sum);
    } catch (e) {
      enqueueSnackbar('Could not load supplier settlements', { variant: 'warning' });
    } finally {
      setLoading(false);
    }
  }, [dispatch, cheque._id]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const afterChange = () => {
    load();
    onChanged();
  };

  const handleDelete = async () => {
    if (!deleting) return;
    deleteLoading.onTrue();
    try {
      await dispatch(
        deleteChequeSettlement({ chequeId: cheque._id, settlementId: deleting._id })
      ).unwrap();
      enqueueSnackbar('Settlement deleted');
      setDeleting(undefined);
      afterChange();
    } catch (e) {
      enqueueSnackbar(typeof e === 'string' ? e : e?.message || 'Failed to delete settlement', {
        variant: 'error',
      });
    } finally {
      deleteLoading.onFalse();
    }
  };

  const outstanding = summary?.settlementOutstandingInCents ?? 0;
  const net = summary?.companyNetPositionInCents ?? 0;

  return (
    <Box mt={2}>
      <Box sx={{ p: 2, bgcolor: 'background.neutral', borderRadius: 1, mb: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle2">Supplier Settlement</Typography>
          {summary && (
            <Stack direction="row" spacing={1}>
              <Label
                color={
                  (summary.supplierSettlementStatus === 'Settled' && 'success') ||
                  (summary.supplierSettlementStatus === 'Partially Settled' && 'warning') ||
                  'error'
                }
                variant="soft"
              >
                {summary.supplierSettlementStatus}
              </Label>
              {summary.isClosed && (
                <Label color="success" variant="soft">
                  Closed
                </Label>
              )}
            </Stack>
          )}
        </Stack>

        {loading && !summary ? (
          <CircularProgress size={24} />
        ) : (
          summary && (
            <Stack spacing={1}>
              <Row label="Cheque Amount" value={fCurrencyRupees(summary.amountInCents)} />
              <Row
                label="Paid to Supplier by Company"
                value={fCurrencyRupees(summary.settledByCompanyInCents)}
              />
              <Row
                label="Paid to Supplier by Debtor Directly"
                value={fCurrencyRupees(summary.settledByDebtorDirectInCents)}
              />
              <Row
                label="Still Owed to Supplier"
                value={fCurrencyRupees(outstanding)}
                color="warning.main"
              />
              <LinearProgress
                variant="determinate"
                value={Math.min((summary.settledInCents / summary.amountInCents) * 100, 100)}
                sx={{ height: 8, borderRadius: 1, my: 1 }}
              />
              <Row
                label="Recovered from Debtor"
                value={`${fCurrencyRupees(summary.recoveredInCents)} (Rs.${(
                  summary.recoveryOutstandingInCents / 100
                ).toLocaleString('en-US', { minimumFractionDigits: 2 })} left)`}
              />
              <Row
                label={net < 0 ? 'Company Out of Pocket' : 'Company Net Position'}
                value={fCurrencyRupees(Math.abs(net))}
                color={net < 0 ? 'error.main' : 'success.main'}
              />
            </Stack>
          )
        )}
      </Box>

      {outstanding > 0 && (
        <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<Iconify icon="mingcute:add-line" />}
            onClick={() => {
              setEditing(undefined);
              showDialog.onTrue();
            }}
          >
            Settle Supplier
          </Button>
        </Box>
      )}

      <Typography variant="subtitle2" gutterBottom>
        Settlement History ({settlements.length})
      </Typography>

      {!loading && settlements.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
          No payments made to the supplier yet
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {settlements.map((s) => {
            const isDirect = s.source === ChequeSettlementSource.DEBTOR_DIRECT;
            return (
              <Card key={s._id} variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" color="primary">
                      {fCurrencyRupees(s.amountInCents)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {fDate(s.settlementDate)}
                    </Typography>
                  </Stack>
                  {s.note && (
                    <Typography variant="body2" color="text.secondary">
                      {s.note}
                    </Typography>
                  )}
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Label color={isDirect ? 'info' : 'default'} variant="soft">
                        {isDirect ? 'Paid by debtor directly' : 'Paid by company'}
                      </Label>
                      <Typography variant="caption" color="text.disabled">
                        {s.createdBy?.firstName} {s.createdBy?.lastName}
                      </Typography>
                    </Stack>
                    {isDirect ? (
                      <Typography variant="caption" color="text.disabled">
                        Edit via the recovery payment
                      </Typography>
                    ) : (
                      <Stack direction="row">
                        <IconButton
                          size="small"
                          onClick={() => {
                            setEditing(s);
                            showDialog.onTrue();
                          }}
                        >
                          <Iconify icon="solar:pen-bold" />
                        </IconButton>
                        <IconButton size="small" color="error" onClick={() => setDeleting(s)}>
                          <Iconify icon="solar:trash-bin-trash-bold" />
                        </IconButton>
                      </Stack>
                    )}
                  </Stack>
                </Stack>
              </Card>
            );
          })}
        </Stack>
      )}

      {showDialog.value && (
        <SettlementDialog
          key={editing?._id ?? 'new'}
          cheque={cheque}
          settlement={editing}
          isOpen={showDialog.value}
          onClose={showDialog.onFalse}
          onSuccess={afterChange}
          outstandingAmount={outstanding + (editing?.amountInCents ?? 0)}
        />
      )}

      <ConfirmDialog
        open={!!deleting}
        onClose={() => setDeleting(undefined)}
        title="Delete settlement"
        content={`Delete the ${
          deleting ? fCurrencyRupees(deleting.amountInCents) : ''
        } settlement? The amount will be owed to the supplier again.`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={deleteLoading.value}
          >
            Delete
          </Button>
        }
      />
    </Box>
  );
}
